const RECOVERY_KEY = 'masira:load-recovery';
const RECOVERY_PARAM = '__masira_reload';

export function isPreviewRuntime() {
  return import.meta.env.DEV || /\.(lovableproject(?:-dev)?\.com|lovable\.app|gpt-eng\.com|gptengineer\.run)$/.test(location.hostname);
}

export function isRecoverableLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|module script|loading chunk|chunkloaderror|invalid hook call|Cannot read properties of null \(reading 'use(?:State|Effect|Context|Ref|Memo|Reducer)'\)/i.test(message);
}

// Only one automatic retry per unsuccessful startup; never clear login storage.
export function recoverAppLoad(error: unknown): boolean {
  if (!isRecoverableLoadError(error)) return false;
  try {
    if (sessionStorage.getItem(RECOVERY_KEY) || new URL(location.href).searchParams.has(RECOVERY_PARAM)) return false;
    sessionStorage.setItem(RECOVERY_KEY, '1');
    reloadApp();
    return true;
  } catch {
    // Blocked browser storage must not cause an endless reload loop.
    return false;
  }
}

export function reloadApp() {
  const url = new URL(location.href);
  url.searchParams.set(RECOVERY_PARAM, Date.now().toString());
  location.replace(url.href);
}

export function markAppHealthy() {
  try {
    sessionStorage.removeItem(RECOVERY_KEY);
    const url = new URL(location.href);
    url.searchParams.delete(RECOVERY_PARAM);
    history.replaceState(history.state, '', url.href);
  } catch { /* Recovery still works when storage is unavailable. */ }
}

export async function clearPreviewWorker() {
  if (!isPreviewRuntime()) return;
  // Older published previews registered a cache-first worker which can serve
  // modules from different Vite optimizer generations, even after a reload.
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.filter((registration) =>
        [registration.active, registration.waiting, registration.installing].some((worker) =>
          worker && new URL(worker.scriptURL).pathname === '/service-worker.js'
        )
      ).map((registration) => registration.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((key) => key.startsWith('masira-shell-')).map((key) => caches.delete(key)));
    }
  } catch { /* Private browsing may deny access to service workers or caches. */ }
}